import { useHistory } from 'react-router-dom';
import { maskParams } from '../../../../util/urlParamMasking';

const formatDateForDisplay = value => {
  if (!value) return '';

  const rawValue = String(value).trim();
  if (!rawValue) return '';

  const isoDateMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    const localDate = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
  }

  const parsedDate = new Date(rawValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return rawValue;
};

const formatTimeForDisplay = value => {
  if (!value) return '';

  const rawValue = String(value).trim();
  if (!rawValue) return '';

  const twentyFourHourMatch = rawValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFourHourMatch) {
    const hours = Number(twentyFourHourMatch[1]);
    const minutes = Number(twentyFourHourMatch[2]);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
  }

  return rawValue.replace(/^(\d{1,2}:\d{2}):\d{2}(\s*[AP]M)$/i, '$1$2');
};

const parseDateTimeParts = value => {
  if (!value) return { date: '', time: '' };

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return { date: '', time: '' };
  }

  return {
    date: parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: parsedDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
};

/**
 * Custom hook to handle navigation with email verification checks
 *
 * @param {Function} checkEmailVerification - Function to check if user's email is verified
 * @returns {Object} Object containing navigation handler functions
 */
const useNavigationHandlers = checkEmailVerification => {
  const history = useHistory();

  const buildEventDisplayContext = event => {
    const rawEventDate =
      event?.rawEventDate ||
      event?.event_date ||
      event?.localDate ||
      event?.local_date ||
      event?.eventDate?.fullDate ||
      event?.datetime_utc ||
      event?.date ||
      null;

    let eventDate = formatDateForDisplay(
      event?.rawEventDate ||
        event?.event_date ||
        event?.localDate ||
        event?.local_date ||
        event?.eventDate?.localDate ||
        event?.eventDate?.fullDate ||
        ''
    );
    let eventTime = formatTimeForDisplay(
      event?.event_time ||
        event?.localTime ||
        event?.local_time ||
        event?.eventDate?.localTime ||
        event?.eventDate?.time ||
        event?.time ||
        ''
    );

    if (typeof event?.dateTime === 'string' && event.dateTime.trim()) {
      const [datePart, timePart = ''] = event.dateTime.split('@');
      if (!eventDate) eventDate = datePart.trim();
      if (!eventTime && event.dateTime.includes('@')) eventTime = timePart.trim();
    }

    if (!eventDate && typeof event?.date === 'string' && event.date.trim()) {
      eventDate = event.date.trim();
    }

    if (!eventTime && typeof event?.time === 'string' && event.time.trim()) {
      eventTime = event.time.trim();
    }

    if ((!eventDate || !eventTime) && (event?.eventDate?.fullDate || event?.datetime_utc)) {
      const parsedParts = parseDateTimeParts(event?.eventDate?.fullDate || event?.datetime_utc);
      if (!eventDate) eventDate = parsedParts.date;
      if (!eventTime) eventTime = parsedParts.time;
    }

    let eventVenue =
      event?.venue_name || event?.location?.name || event?.venueName || event?.venue || '';
    let eventLocation =
      event?.locationDescription ||
      [
        event?.venue_city || event?.location?.city || null,
        event?.venue_state || event?.location?.stateCode || event?.location?.state || null,
      ]
        .filter(Boolean)
        .join(', ');

    if (
      (!eventVenue || !eventLocation) &&
      typeof event?.venueLocation === 'string' &&
      event.venueLocation.trim()
    ) {
      const [venuePart, locationPart = ''] = event.venueLocation.split('|');
      if (!eventVenue) {
        eventVenue = venuePart.trim();
      }
      if (!eventLocation) {
        eventLocation = locationPart.trim();
      }
    }

    const heroImageUrl =
      event?.image ||
      event?.image_url ||
      event?.event_image ||
      event?.attraction_image_url ||
      event?.attractionImageUrl ||
      null;

    return {
      rawEventDate,
      eventDate,
      eventTime,
      eventVenue: eventVenue || 'Venue TBD',
      eventLocation: eventLocation || 'Location TBD',
      heroImageUrl,
    };
  };

  /**
   * Handle search navigation
   * @param {string} searchTerm - The search query
   */
  const handleSearch = () => {};

  /**
   * Handle event click navigation from autocomplete
   * Navigate to EventListingsPage with event name as search query
   *
   * @param {Object} event - The event object (Ticketmaster or SeatGeek)
   */
  const handleEventClick = event => {
    checkEmailVerification(() => {
      const {
        rawEventDate,
        eventDate,
        eventTime,
        eventVenue,
        eventLocation,
        heroImageUrl,
      } = buildEventDisplayContext(event);

      if (event?.isSupabaseEvent && event?.id) {
        history.push(`/tickets?eventId=${encodeURIComponent(event.id)}`, {
          eventName: event.event_name || event.name || 'Event',
          eventDate,
          rawEventDate,
          eventTime,
          eventVenue,
          eventLocation,
          heroImageUrl,
          clickedEvent: event,
          eventSource: 'supabase',
          attractionName: event.attractionName || null,
        });
        return;
      }

      const eventName =
        event?.title || event?.attributes?.title || event?.event_name || event?.name || '';
      const ticketmasterId =
        event?.ticketmasterId || event?.id || event?.attributes?.publicData?.ticketmasterId || null;
      const ticketmasterAttractionId =
        event?.ticketmasterAttractionId ||
        event?.attractionId ||
        event?.attributes?.publicData?.ticketmasterAttractionId ||
        null;

      const params = maskParams({
        ticketmasterId,
        ticketmasterAttractionId,
        eventName,
        eventDate,
        eventTime,
        venue: eventVenue,
        location: eventLocation,
      });

      history.push(`/tickets?${params.toString()}`, {
        eventName: eventName || 'Event',
        eventDate,
        rawEventDate,
        eventTime,
        eventVenue,
        eventLocation,
        heroImageUrl,
        clickedEvent: event,
        eventSource: event?.source || 'ticketmaster',
        ticketmasterId,
        ticketmasterAttractionId,
      });
    });
  };

  /**
   * Handle attraction click navigation from autocomplete
   * Navigates to events page filtered by attraction ID
   *
   * @param {Object} attraction - The attraction object with id and name
   */
  const handleAttractionClick = attraction => {
    checkEmailVerification(() => {
      const internalParams = {};
      const attractionImageUrl =
        attraction?.image ||
        attraction?.image_url ||
        attraction?.attraction_image_url ||
        attraction?.attractionImageUrl ||
        null;

      if (attraction?.searchIntent === 'venue' && attraction?.venueQuery) {
        internalParams.intent = 'venue';
        internalParams.venue = attraction.venueQuery;
        internalParams.keywords = attraction.venueQuery;
        if (attraction.ticketmasterVenueId) {
          internalParams.ticketmasterVenueId = attraction.ticketmasterVenueId;
        }
      } else if (
        (attraction?.source === 'ticketmaster' || attraction?.source === 'ticketmaster_catalog') &&
        attraction?.ticketmasterAttractionId
      ) {
        internalParams.ticketmasterAttractionId = attraction.ticketmasterAttractionId;
        if (attraction.name) {
          internalParams.keywords = attraction.name;
        }
      } else if (attraction?.id) {
        internalParams.attractionId = attraction.id;
      } else if (attraction?.name) {
        internalParams.keywords = attraction.name;
      }
      if (attractionImageUrl) {
        internalParams.attractionImageUrl = attractionImageUrl;
      }

      history.push(`/events?${maskParams(internalParams).toString()}`, {
        selectedAttraction: attraction,
        attractionImageUrl,
      });
    });
  };

  /**
   * Handle crew click navigation
   * Matches CrewsPage behavior - navigates to CrewPage
   *
   * @param {Object} crew - The crew object with id and name
   */
  const handleCrewClick = crew => {
    checkEmailVerification(() => {
      // Use crew ID as the route parameter
      // CrewPage expects /crew/{crewname} format
      const crewSlug = crew.id || crew.name.toLowerCase().replace(/\s+/g, '-');
      history.push(`/crew/${crewSlug}`);
    });
  };

  /**
   * Handle join crew action
   * Currently logs the action - can be enhanced with Redux/API calls
   *
   * @param {string} crewId - The crew ID to join
   */
  const handleJoinCrew = crewId => {
    checkEmailVerification(() => {
      // TODO: Implement actual crew joining logic
      // Could dispatch Redux action or make API call
      // For now, just navigate to the crew page
      history.push(`/crew/${crewId}`);
    });
  };

  return {
    handleSearch,
    handleEventClick,
    handleAttractionClick,
    handleCrewClick,
    handleJoinCrew,
  };
};

export default useNavigationHandlers;
